import { labelFor } from "components/BeneficiaryFormContainer/PersonalInfoStep";
import CountryAutocomplete from "components/CountryAutocomplete";
import LoadingDots from "components/LoadingDots";
import NumberFormComponent from "components/shared/FormComponent/NumberFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import { mandatoryURSSAFFields } from "components/shared/Representatives/RepresentativeForm";
import ScrollShadowWrapper from "components/shared/ScrollShadowWrapper";
import type { IBeneficiar, IRepresentative } from "containers/Beneficiars/types";
import { isNIRWrong, URSSAFBeneficiaryError } from "containers/DeclarationPage/utils";
import type { TCountries } from "countries";
import { Body } from "helpers/TypographyComponents";
import { isObjectEmpty } from "helpers/utils";
import { t } from "i18next";
import type { Dispatch, SetStateAction } from "react";
import { forwardRef, useEffect, useMemo } from "react";
import type { FieldErrors } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { fetchBeneficiar } from "store/beneficiaryStore";
import { useAppDispatch, useAppSelector } from "store/store";

import type { FormRefs, TFormattedData, TSubmitData } from "..";
import styles from "./style.module.scss";

export type BeneficiaryFormValues = {
    firstname: string | undefined;
    surname: string | undefined;
    socialSecurityNumber: string | undefined;
    siretNumber: string | undefined;
    street: string | undefined;
    zipCode: string | undefined;
    city: string | undefined;
    country: { name: string; flag: string };
};

export const getErrorI18nTable = (): Record<string, string> => {
    return {
        UnauthorizedProfileType: t("URSSAFErrors.UnauthorizedProfileType"),
        MissingMandatoryField: t("URSSAFErrors.MissingMandatoryField"),
        WrongDateFormat: t("URSSAFErrors.WrongDateFormat"),
        WrongLength: t("URSSAFErrors.WrongLength"),
        WrongControlKey: t("URSSAFErrors.WrongControlKey"),
        RequiredField: t("URSSAFErrors.RequiredField"),
        WrongValue: t("URSSAFErrors.WrongValue"),
    };
};

interface IFixesForm {
    /**
     * Id of the beneficiary (either being updated or linked to representative).
     */
    beneficiaryId: number | undefined;
    /**
     * Type of individual being updated.
     */
    type: "representatives" | "beneficiary";
    /**
     * Data of the individual being updated.
     */
    data: TFormattedData;
    /**
     * Full data for final submit (combines data of all forms.)
     */
    submitData: TSubmitData;
    /**
     * Saves full data for final submit.
     */
    setSubmitData: Dispatch<SetStateAction<TSubmitData>>;
    /**
     * Full errors (combines errors of all forms.)
     */
    formErrors: Record<number, FieldErrors<BeneficiaryFormValues>>;
    setFormErrors: Dispatch<SetStateAction<Record<number, FieldErrors<BeneficiaryFormValues>>>>;
    /**
     * Saves refs of each form (needed to scroll to fields with errors.)
     */
    setFormRefs: Dispatch<SetStateAction<FormRefs>>;
}

export interface FormContainerRefs {
    [key: string]: HTMLDivElement | null;
}

/**
 * Form for each person (beneficiary and/or representative) that needs to be updated.
 */
const IndividualForm = forwardRef<HTMLFormElement | null, IFixesForm>(function IndividualForm(props: IFixesForm, ref) {
    const { beneficiaryId, type, data, setSubmitData, submitData, setFormErrors, formErrors, setFormRefs } = props;
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    /**
     * Object that contains refs for each field within form.
     */
    const formContainerRef: FormContainerRefs = {};

    const { beneficiar: beneficiary, beneficiaryStatus } = useAppSelector((state) => state.beneficiary);
    const beneficiaryDataLoading = ["idle", "pending"].includes(beneficiaryStatus);

    const personData = type === "beneficiary" ? beneficiary : data;

    const defaultValues: BeneficiaryFormValues = useMemo(() => {
        return {
            firstname: personData?.name ?? "",
            surname: personData?.surname ?? "",
            socialSecurityNumber: personData?.socialSecurityNumber ?? "",
            siretNumber: personData?.siretNumber ?? "",
            street: personData?.street ?? "",
            zipCode: personData?.zipCode ?? "",
            city: personData?.city ?? "",
            country: { name: personData?.country ?? "", flag: "" },
        };
    }, [beneficiary, personData]);

    const {
        control,
        setError,
        formState: { errors },
        watch,
        reset,
        setValue,
        clearErrors,
    } = useForm({ defaultValues, mode: "all" });

    /**
     * When loading form, set errors from URSSAF.
     */
    const setBeneficiaryErrors = async () => {
        if (data.errors) {
            await data.errors.forEach((error) => {
                if (error.field in defaultValues) {
                    setError(error.field as any, { message: getErrorI18nTable()[error.message] });
                }
            });
        }
    };

    useEffect(() => {
        if (type === "representatives") {
            setBeneficiaryErrors();
        }
    }, []);

    // Needed to fetch beneficiary because beneficiary data in IPayment isn't complete, which causes backend error when updating.
    useEffect(() => {
        if (type === "beneficiary" && beneficiaryId) {
            dispatch(fetchBeneficiar(beneficiaryId)).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    const benef = resp.payload;
                    reset({
                        firstname: benef?.name ?? "",
                        surname: benef?.surname ?? "",
                        socialSecurityNumber: benef?.socialSecurityNumber ?? "",
                        siretNumber: benef?.siretNumber ?? "",
                        street: benef?.street ?? "",
                        zipCode: benef?.zipCode ?? "",
                        city: benef?.city ?? "",
                        country: { name: benef?.country ?? "", flag: "" },
                    });
                    setBeneficiaryErrors();
                }
            });
        }
    }, [beneficiaryId]);

    /**
     * Saves new data (after change) into submitData which is what will be used in the general submit:
     * @param personData Data of person as saved within submitData.
     * @param element Field name and its new value.
     */
    const handleSaveData = (personData: IBeneficiar | IRepresentative, element: { name: string; value: string }) => {
        // Found out we change it to firstname to match the backend.
        const key = element.name === "firstname" ? "name" : element.name;
        const data = { ...submitData, [key]: element.value };
        setSubmitData(data);
    };

    /**
     * Handles any changes by resetting value within form and then triggering saving within submitData.
     * @param e Either event (for simple fields) or data from country chosen.
     */
    const handleChanges = async (e: React.ChangeEvent<HTMLInputElement> | TCountries) => {
        const personData = submitData;
        // If the property flag exists, that means it's the country field that was changed (Autocomplete handles onChange differently.):
        if (e && (e as TCountries).flag) {
            await setValue("country", e as TCountries, { shouldValidate: true });
            personData && handleSaveData(personData, { name: "country", value: (e as TCountries).name });
        } else {
            // For all other fields, the simple event is used:
            const fieldName = (e as any).target.name;
            const fieldValue = (e as any).target.value;
            await setValue(fieldName, fieldValue);
            personData && handleSaveData(personData, { name: fieldName, value: fieldValue });

            // For NIR, check if NIR typed is correct otherwise force error:
            if (fieldName === "socialSecurityNumber") {
                if (isNIRWrong(fieldValue) || fieldValue === "") {
                    setError(fieldName, {
                        message: isNIRWrong(fieldValue) ? getErrorI18nTable().WrongLength : t("NewElement.Required"),
                    });
                } else {
                    clearErrors(fieldName);
                }
            } else if (fieldName === "siretNumber" && fieldValue.length !== 14) {
                setError(fieldName, {
                    message: t("Declarations.BeneficiariesTable.SiretError"),
                });
            } else {
                // For all others clear errors as long as a value is filled. (Errors not cleared correctly as I'm bypassing natural onChange.)
                const isRequired = mandatoryURSSAFFields[fieldName].includes(data.socialProfileType);
                isRequired && fieldValue === ""
                    ? setError(fieldName, { message: t("NewElement.Required") })
                    : clearErrors(fieldName);
            }
        }
    };

    // This sends errors back to parent component to gather all errors from forms and know on submit if an error persists (as I'm not using the submit function).
    useEffect(() => {
        const newErrors = formErrors;
        if (isObjectEmpty(formErrors)) {
            // If formErrors is empty, pass initial errors:
            setFormErrors((prev) => ({ ...prev, [data.id]: errors }));
        } else {
            // After change, if there are no more errors, delete person key:
            if (isObjectEmpty(errors)) {
                delete newErrors[data.id];
            } else {
                // If errors still exist, replace with new errors:
                newErrors[data.id] = errors;
            }
            setFormErrors(newErrors);
        }
    }, [Object.keys(errors)]);

    // Saves refs of each field to pass it on to parent component:
    useEffect(() => {
        if (formContainerRef) {
            setFormRefs((prev) => ({ ...prev, [data.id]: { ...formContainerRef } }));
        }
    }, []);

    const isLoading = type === "beneficiary" && beneficiaryDataLoading;
    const shadowWrapperStyle = isLoading ? {} : { maxHeight: "405px" };

    return (
        <ScrollShadowWrapper style={shadowWrapperStyle}>
            <form className={styles["individual-form"]} ref={ref} id={`form-${data.id}`}>
                {/* Loading only displayed for beneficiary as a fetch is done (for representatives is passed as prop.) */}
                {isLoading ? (
                    <div className={styles["loading-text"]}>
                        <Body>{t("Declarations.BeneficiariesTable.LoadingData")}</Body>
                        <LoadingDots />
                    </div>
                ) : (
                    <>
                        <TextFormComponent
                            // There is no entity on the representative. For now, we set it to default labels (aka Prénom/Nom)
                            label={labelFor()[(personData as IBeneficiar).entity ?? "physical"].name}
                            control={control}
                            controlName="firstname"
                            error={errors.firstname}
                            onChange={handleChanges}
                        />
                        <TextFormComponent
                            label={labelFor()[(personData as IBeneficiar).entity ?? "physical"].surname}
                            control={control}
                            controlName="surname"
                            error={errors.surname}
                            onChange={handleChanges}
                        />
                        <div>
                            <NumberFormComponent
                                label={t("Beneficiars.SecurityNumber")}
                                control={control}
                                controlName="socialSecurityNumber"
                                required={mandatoryURSSAFFields.NIR.includes(data?.socialProfileType ?? "")}
                                error={errors.socialSecurityNumber || isNIRWrong(watch("socialSecurityNumber"))}
                                onChangeWithEvent={handleChanges}
                                ref={(el: HTMLDivElement | null) => (formContainerRef[`socialSecurityNumber`] = el)}
                            />
                            {isNIRWrong(watch("socialSecurityNumber")) && (
                                <div className={styles["nir-warning"]}>
                                    {t("NewBeneficiary.SocialSecurityValidity")}
                                </div>
                            )}
                        </div>
                        <NumberFormComponent
                            control={control}
                            controlName="siretNumber"
                            label={t("Beneficiars.Siret")}
                            error={errors.siretNumber}
                            onChangeWithEvent={handleChanges}
                            ref={(el: HTMLDivElement | null) => (formContainerRef[`siretNumber`] = el)}
                        />
                        <TextFormComponent
                            control={control}
                            controlName="street"
                            label={t("NewBeneficiary.StreetNumber")}
                            required={mandatoryURSSAFFields.street.includes(data?.socialProfileType ?? "")}
                            error={errors.street}
                            onChange={handleChanges}
                            placeholder={t("AddressPlaceholders.Street")}
                            ref={(el: HTMLDivElement | null) => (formContainerRef[`street`] = el)}
                        />
                        <TextFormComponent
                            control={control}
                            controlName="zipCode"
                            label={t("NewBeneficiary.ZIPCode")}
                            placeholder={t("AddressPlaceholders.ZipCode")}
                            required={mandatoryURSSAFFields.zipCode.includes(data?.socialProfileType ?? "")}
                            error={errors.zipCode}
                            onChange={handleChanges}
                            ref={(el: HTMLDivElement | null) => (formContainerRef[`zipCode`] = el)}
                        />
                        <TextFormComponent
                            control={control}
                            controlName="city"
                            placeholder={t("AddressPlaceholders.City")}
                            label={t("NewBeneficiary.City")}
                            required={mandatoryURSSAFFields.city.includes(data?.socialProfileType ?? "")}
                            error={errors.city}
                            onChange={handleChanges}
                            ref={(el: HTMLDivElement | null) => (formContainerRef[`city`] = el)}
                        />
                        <CountryAutocomplete
                            control={control}
                            errors={errors}
                            required
                            onChange={handleChanges}
                            ref={(el: HTMLDivElement | null) => (formContainerRef[`country`] = el)}
                        />
                    </>
                )}
            </form>
        </ScrollShadowWrapper>
    );
});

export default IndividualForm;
